import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useAuth } from '@/app/auth-provider';
import { Job, type WeekSelectorValue } from '@/components/shared/WeekSelector';
import { useProject } from '@/contexts/ProjectContext';

// Module-level cache - keyed by project_id
const jobsCache = new Map<string, { jobs: Job[]; timestamp: number }>();
const jobsRequestCache = new Map<string, Promise<Job[]>>();

// Cache duration: 60 minutes (jobs list changes infrequently)
const CACHE_DURATION_MS = 60 * 60 * 1000;

async function fetchJobsFromApi({
  projectId,
  token,
  force = false,
}: {
  projectId: string;
  token?: string;
  force?: boolean;
}): Promise<Job[]> {
  const cached = jobsCache.get(projectId);

  if (!force && cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
    return cached.jobs;
  }

  const requestKey = `${projectId}:${force ? 'force' : 'cached'}`;
  const inFlightRequest = jobsRequestCache.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const request = (async() => {
    const headers: HeadersInit = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const res = await fetch(`/api/projects/${projectId}/jobs`, { headers });

    if (!res.ok) {
      throw new Error('Failed to fetch jobs');
    }

    const data = await res.json();
    const jobsList: Job[] = data.jobs || [];
    jobsCache.set(projectId, { jobs: jobsList, timestamp: Date.now() });
    return jobsList;
  })();

  jobsRequestCache.set(requestKey, request);
  try {
    return await request;
  } finally {
    jobsRequestCache.delete(requestKey);
  }
}

/**
 * Hook to fetch pipeline jobs for the current project.
 * Uses simple Firestore query via /api/projects/[projectId]/jobs - fast!
 *
 * Optimized to prevent data "blinks" during project transitions:
 * - Maintains old data during transitions
 * - Only clears data when new data arrives
 *
 * **`jobsTrustworthy`:** while `jobsDatasetProjectId !== projectId`, the `jobs` array may still
 * be from the previous project — do not derive `job_ids` / week scope for API calls until true.
 * Pair with {@link useResetWeekSelectionOnProjectChange} so week pickers do not keep prior-project
 * job IDs after switching projects.
 */
export function usePipelineJobs() {
  const { user } = useAuth();
  const { currentProject, loading: projectLoading, isTransitioning } = useProject();
  
  const projectId = currentProject?.id || null;
  
  // Track which project ID the current data belongs to
  const dataProjectIdRef = useRef<string | null>(null);
  const activeRequestVersionRef = useRef(0);

  const [jobsDatasetProjectId, setJobsDatasetProjectId] = useState<string | null>(() => {
    if (!projectId) return null;
    const cached = jobsCache.get(projectId);
    if (cached?.jobs) return projectId;
    return null;
  });

  const [jobs, setJobs] = useState<Job[]>(() => {
    if (!projectId) return [];
    const cached = jobsCache.get(projectId);
    if (cached?.jobs) {
      dataProjectIdRef.current = projectId;
      return cached.jobs;
    }
    return [];
  });
  const [initialLoading, setInitialLoading] = useState(!projectId || !jobsCache.has(projectId));
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchJobs = useCallback(async(targetProjectId: string, force = false): Promise<Job[]> => {
    if (!targetProjectId) {
      return [];
    }

    const token = user ? await user.getIdToken() : undefined;
    return fetchJobsFromApi({ projectId: targetProjectId, token, force });
  }, [user]);

  // Load when project changes
  useEffect(() => {
    const requestVersion = ++activeRequestVersionRef.current;
    let cancelled = false;
    const isLatestRequest = () =>
      !cancelled && activeRequestVersionRef.current === requestVersion;

    if (projectLoading) return;
    
    if (!projectId) {
      // Only clear if we're not transitioning (avoid blink during switch)
      if (!isTransitioning) {
        setJobs([]);
        dataProjectIdRef.current = null;
        setJobsDatasetProjectId(null);
      }
      setInitialLoading(false);
      setIsRefreshing(false);
      return () => {
        cancelled = true;
      };
    }

    // Use cache if valid
    const cached = jobsCache.get(projectId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION_MS) {
      if (isLatestRequest()) {
        setJobs(cached.jobs);
        dataProjectIdRef.current = projectId;
        setJobsDatasetProjectId(projectId);
        setInitialLoading(false);
        setIsRefreshing(false);
      }
      return () => {
        cancelled = true;
      };
    }

    const hasCurrentProjectJobs = dataProjectIdRef.current === projectId && jobs.length > 0;
    // `jobs` may still list the previous project until the fetch below resolves.
    if (!hasCurrentProjectJobs) {
      setJobsDatasetProjectId(null);
    }
    setInitialLoading(!hasCurrentProjectJobs);
    setIsRefreshing(hasCurrentProjectJobs);
    setError(null);

    fetchJobs(projectId)
      .then((newJobs) => {
        if (!isLatestRequest()) return;
        setJobs(newJobs);
        dataProjectIdRef.current = projectId;
        setJobsDatasetProjectId(projectId);
      })
      .catch((err) => {
        if (!isLatestRequest()) return;
        console.error('Failed to load jobs:', err);
        setError(err.message);
      })
      .finally(() => {
        if (!isLatestRequest()) return;
        setInitialLoading(false);
        setIsRefreshing(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, projectId, projectLoading, isTransitioning, fetchJobs, jobs.length]);

  const refresh = useCallback(async() => {
    if (!projectId) return;

    const requestVersion = ++activeRequestVersionRef.current;
    const isLatestRequest = () => activeRequestVersionRef.current === requestVersion;
    
    const hasCurrentProjectJobs = dataProjectIdRef.current === projectId && jobs.length > 0;
    setInitialLoading(!hasCurrentProjectJobs);
    setIsRefreshing(hasCurrentProjectJobs);
    setError(null);
    try {
      const jobsList = await fetchJobs(projectId, true);
      if (!isLatestRequest()) return;
      setJobs(jobsList);
      dataProjectIdRef.current = projectId;
      setJobsDatasetProjectId(projectId);
    } catch (err) {
      if (!isLatestRequest()) return;
      setError(err instanceof Error ? err.message : 'Failed to refresh');
    } finally {
      if (!isLatestRequest()) return;
      setInitialLoading(false);
      setIsRefreshing(false);
    }
  }, [fetchJobs, projectId, jobs.length]);

  const jobsTrustworthy = Boolean(
    projectId && jobsDatasetProjectId === projectId,
  );

  return {
    jobs,
    loading: initialLoading || projectLoading,
    initialLoading: initialLoading || projectLoading,
    isRefreshing,
    error,
    refresh,
    projectId,
    jobsDatasetProjectId,
    jobsTrustworthy,
  };
}

/**
 * Clears week scope when the active project changes so job_ids never carry across projects.
 * Use on any page that stores {@link WeekSelectorValue} next to {@link usePipelineJobs}.
 */
export function useResetWeekSelectionOnProjectChange(
  setWeekSelection: Dispatch<SetStateAction<WeekSelectorValue>>,
) {
  const { currentProject, loading: projectLoading } = useProject();
  const projectId = currentProject?.id ?? null;
  const prevProjectIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (projectLoading) return;
    const prev = prevProjectIdRef.current;
    if (prev !== null && prev !== projectId) {
      setWeekSelection('all');
    }
    prevProjectIdRef.current = projectId;
  }, [projectId, projectLoading, setWeekSelection]);
}

// Clear cache when new job is created
export function clearPipelineJobsCache(projectId?: string) {
  if (projectId) {
    jobsCache.delete(projectId);
  } else {
    jobsCache.clear();
  }
}

/**
 * Standalone function to fetch jobs with caching.
 * Used by other hooks to share the same cache.
 */
export async function fetchCachedJobs(
  token: string,
  projectId: string,
  force = false,
): Promise<Job[]> {
  return fetchJobsFromApi({ projectId, token, force });
}
